import type { GameState } from '../state';
import { D, ZERO, add, mul, div, pow, floor, type Decimal } from '../math/num';
import { FRAMES, getFrame } from '../data/frames';
import { overlordForTier, type OverlordDef } from '../data/overlords';
import { SWARM_COMPLETE } from '../data/harvest';

/**
 * Ascension — the prestige engine (CONCEPT §5). Upload the Mind into the next
 * chassis: the Mind (graph + parts + inventory + Components) PERSISTS; the run
 * (frame level, Scrap, wave) resets; you earn Cores. Cores use fractional-
 * exponent scaling so each ascension must out-earn the last (ANATOMY §2).
 *
 * The gate is an OVERLORD, not a wave count: beating the overlord that owns
 * the next frame authorizes the upload (SCOPE_Overlords_and_Pets.md).
 */

export function isMaxTier(state: GameState): boolean {
  return getFrame(state.frame.defId).tier >= FRAMES.length;
}

/** The overlord gating the next upload (null at max tier). */
export function ascendGate(state: GameState): OverlordDef | null {
  if (isMaxTier(state)) return null;
  return overlordForTier(getFrame(state.frame.defId).tier) ?? null;
}

export function canAscend(state: GameState): boolean {
  const gate = ascendGate(state);
  if (!gate) return false;
  return state.overlords.defeated.includes(gate.id);
}

/** Cores awarded for ascending right now (the live "+X on Ascend" projection). */
export function coresOnAscend(state: GameState): Decimal {
  const tier = getFrame(state.frame.defId).tier;
  const wave = state.combat.highestWave;
  if (wave < 2) return ZERO;
  // floor( wave^1.2 / 8 * tier ) — small but compounding; out-earns prior runs.
  const base = div(pow(D(wave), D(1.2)), D(8));
  return floor(mul(base, D(tier)));
}

export interface AscendResult {
  coresGained: Decimal;
  newFrameId: string;
}

/** Reset the run into `frameId` at level 1: Scrap, wave and combat all clear; the
 *  Mind, inventory, Components, Cores, pets and every minigame persist. Shared by
 *  Ascension (into the next chassis) and Reboot (back into the same max chassis). */
function resetRunInto(state: GameState, frameId: string): void {
  const f = getFrame(frameId);
  state.frame = { defId: f.id, level: 1, hp: D(f.baseHp) };
  state.wallet.scrap = ZERO;
  state.combat.wave = 1;
  state.combat.highestWave = 1;
  state.combat.checkpoint = 1;
  state.combat.enemies = [];
  state.combat.attackTimers = {};
  state.combat.maxEffortUntil = 0;
  state.combat.maxEffortCdUntil = 0;
  state.combat.fullSendCdUntil = 0;
  state.combat.autoAdvance = true;
  state.combat.killsThisRun = 0;
}

export function ascend(state: GameState): AscendResult | null {
  if (!canAscend(state)) return null;
  const tier = getFrame(state.frame.defId).tier;
  const gained = coresOnAscend(state);
  const next = FRAMES[tier]!; // FRAMES is 0-indexed; tier N's next is index N

  state.meta.cores = add(state.meta.cores, gained);
  state.meta.ascensions += 1;

  // (Feature unlocks ride on overlord kills now, not on the Ascension itself.)
  // advance chassis; reset the run (the Mind + inventory + Components persist)
  resetRunInto(state, next.id);

  return { coresGained: gained, newFrameId: next.id };
}

/**
 * Reboot — the post-Sol endless Core loop. On the final chassis you can no longer
 * Ascend, so Reboot resets the run for Cores WITHOUT changing chassis. It is gated
 * by the Dyson swarm: each satellite launched grants one reboot, and a completed
 * swarm (all 50) lifts the cap so you can reboot whenever.
 */
export function canReboot(state: GameState): boolean {
  if (!isMaxTier(state) || !state.harvest.unlocked) return false;
  if (state.combat.highestWave < 2) return false; // nothing to bank yet this run
  if (state.harvest.satellites >= SWARM_COMPLETE) return true; // swarm done: uncapped
  return state.meta.reboots < state.harvest.satellites;
}

/** Reboot charges currently available (a finished swarm reads as Infinity). */
export function rebootChargesLeft(state: GameState): number {
  if (!state.harvest.unlocked) return 0;
  if (state.harvest.satellites >= SWARM_COMPLETE) return Infinity;
  return Math.max(0, state.harvest.satellites - state.meta.reboots);
}

export function reboot(state: GameState): AscendResult | null {
  if (!canReboot(state)) return null;
  const gained = coresOnAscend(state);
  const frameId = state.frame.defId; // same max chassis, no upgrade
  state.meta.cores = add(state.meta.cores, gained);
  state.meta.reboots += 1;
  resetRunInto(state, frameId);
  return { coresGained: gained, newFrameId: frameId };
}
